import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Sparkles, Target, Clock, Layers, RefreshCw, Goal, Timer,
  CheckCircle2, XCircle, MinusCircle, Loader2, Ban, CalendarClock,
} from "lucide-react";
import { appTodayIso, addDaysIso } from "../lib/appTime";

const API = process.env.REACT_APP_BACKEND_URL;

const CATS = [
  { id: "main", label: "Picks of the Day", icon: Target,
    key: "main_picks", tint: "amber",
    subtitle: "30 most-certain full-time picks (1X2 / DNB) — highest confidence across the full 20-layer engine." },
  { id: "halftime", label: "Halftime", icon: Clock,
    key: "halftime_picks", tint: "blue",
    subtitle: "30 highest-confidence halftime market picks (HT Result / HT Over-Under)." },
  { id: "double_chance", label: "Double Chance", icon: Layers,
    key: "double_chance_picks", tint: "green",
    subtitle: "30 safest Double Chance picks (1X / 12 / X2)." },
  { id: "btts", label: "BTTS", icon: Goal,
    key: "btts_picks", tint: "amber",
    subtitle: "30 highest-confidence Both Teams To Score (Yes / No) picks across the full match." },
  { id: "halftime_btts", label: "HT BTTS", icon: Timer,
    key: "halftime_btts_picks", tint: "blue",
    subtitle: "30 highest-confidence First-Half Both Teams To Score (Yes / No) picks." },
];

const OutcomeBadge = ({ outcome, status }) => {
  const map = {
    hit: { icon: CheckCircle2, cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "HIT" },
    miss: { icon: XCircle, cls: "bg-red-500/20 text-red-300 border-red-500/40", label: "MISS" },
    void: { icon: MinusCircle, cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/40", label: "VOID" },
    cancelled: { icon: Ban, cls: "bg-rose-500/20 text-rose-300 border-rose-500/40", label: "CANCELLED" },
    postponed: { icon: CalendarClock, cls: "bg-orange-500/20 text-orange-300 border-orange-500/40", label: "POSTPONED" },
    pending: { icon: Loader2, cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", label: "PENDING" },
  };
  const cfg = map[outcome] || map.pending;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-bold ${cfg.cls}`}
      data-testid={`gold-picks-outcome-${outcome}`}
      title={status ? `Fixture status: ${status}` : undefined}
    >
      <Icon className={`w-3 h-3 ${outcome === "pending" ? "animate-spin-slow" : ""}`} />
      {cfg.label}
    </span>
  );
};

const StatChip = ({ label, value, hint, tone = "amber" }) => {
  const palette = {
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-100",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-100",
    blue: "bg-blue-500/10 border-blue-500/30 text-blue-100",
    neutral: "bg-neutral-500/10 border-neutral-500/30 text-neutral-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${palette[tone] || palette.amber}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums leading-tight">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="text-[10px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

const PickRow = ({ pick, index }) => {
  const scoreStr = (pick.actual_home != null && pick.actual_away != null)
    ? `${pick.actual_home}-${pick.actual_away}` : null;
  const confClass = pick.confidence >= 80 ? "text-emerald-300"
    : pick.confidence >= 70 ? "text-amber-200"
    : "text-amber-400";
  return (
    <tr
      className="border-t border-amber-500/10 hover:bg-amber-500/5 transition-colors"
      data-testid={`gold-pick-row-${index}`}
    >
      <td className="px-2 py-2 text-amber-500/60 tabular-nums text-right w-8">{index + 1}</td>
      <td className="px-2 py-2 tabular-nums text-amber-300 text-[11px] whitespace-nowrap">{fmtTime(pick.kickoff)}</td>
      <td className="px-2 py-2 min-w-0">
        <div className="text-[12px] font-semibold text-amber-100 truncate">
          {pick.home} <span className="text-amber-500/50">vs</span> {pick.away}
        </div>
        <div className="text-[10px] text-amber-500/60 truncate">
          {pick.country && <>{pick.country} · </>}{pick.league_name}
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="text-[10px] uppercase tracking-widest text-amber-500/60">{pick.market_type}</div>
        <div className="text-[12px] font-semibold text-amber-100">{pick.prediction}</div>
      </td>
      <td className={`px-2 py-2 text-right tabular-nums font-bold ${confClass}`}>
        {Number(pick.confidence).toFixed(1)}%
      </td>
      <td className="px-2 py-2 text-center">
        <OutcomeBadge outcome={pick.outcome} status={pick.status} />
        {scoreStr && (
          <div className="text-[10px] text-amber-500/60 mt-0.5 tabular-nums">{scoreStr}</div>
        )}
      </td>
    </tr>
  );
};

const CategoryTable = ({ cat, picks }) => {
  if (!picks || picks.length === 0) {
    return (
      <div className="p-6 text-center text-[12px] text-amber-500/60 italic">
        No picks yet — press Refresh to compile.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto max-h-[560px] overflow-y-auto rounded border border-amber-500/15">
      <table className="w-full text-[12px]" data-testid={`gold-picks-table-${cat.id}`}>
        <thead className="sticky top-0 bg-black/95 z-10">
          <tr className="text-amber-500/70 uppercase tracking-widest text-[10px]">
            <th className="text-right px-2 py-2 w-8">#</th>
            <th className="text-left px-2 py-2">Kick</th>
            <th className="text-left px-2 py-2">Match / League</th>
            <th className="text-left px-2 py-2">Pick</th>
            <th className="text-right px-2 py-2">Conf.</th>
            <th className="text-center px-2 py-2">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {picks.map((p, i) => <PickRow key={`${p.fixture_id}-${i}`} pick={p} index={i} />)}
        </tbody>
      </table>
    </div>
  );
};

const DateSelector = ({ date, setDate }) => {
  const todayIso = appTodayIso();
  const opts = [
    { off: -2, label: "-2d" },
    { off: -1, label: "Yesterday" },
    { off: 0, label: "Today" },
    { off: 1, label: "Tomorrow" },
  ];
  return (
    <div className="flex gap-1 flex-wrap items-center">
      {opts.map((o) => {
        const iso = addDaysIso(todayIso, o.off);
        const active = iso === date;
        return (
          <button
            key={o.off}
            onClick={() => setDate(iso)}
            className={`px-3 py-1 text-[11px] rounded uppercase tracking-widest font-semibold border transition-colors ${
              active
                ? "bg-amber-500/30 text-amber-100 border-amber-400"
                : "bg-black/60 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
            }`}
            data-testid={`gold-picks-date-${o.label.toLowerCase()}`}
          >
            {o.label}
          </button>
        );
      })}
      <input
        type="date"
        value={date}
        max={addDaysIso(todayIso, 1)}
        onChange={(e) => e.target.value && setDate(e.target.value)}
        className="h-[26px] bg-black/60 border border-amber-500/25 rounded px-2 text-[11px] text-amber-100 focus:outline-none focus:border-amber-400 [color-scheme:dark]"
        data-testid="gold-picks-date-calendar"
        title="Pick any past date"
      />
    </div>
  );
};

export default function GoldDailyPicksPanel({ token, isAdmin }) {
  const today = appTodayIso();
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("main");
  const [busy, setBusy] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const fetchPicks = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/api/gold/daily-picks`, {
        params: { date },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setLastUpdatedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  // Iter 261 — live hit-tracker.  Poll every 30s while there's at least
  // one pending pick AND the selected date is today or in the past
  // (future dates never resolve, so no need to poll them).
  const hasPending = data && ["main_picks", "halftime_picks",
    "double_chance_picks", "btts_picks", "halftime_btts_picks"]
    .some((k) => (data[k] || []).some((p) => p.outcome === "pending"));
  const isLiveEligible = date <= today && hasPending;

  useEffect(() => {
    if (!isLiveEligible) return undefined;
    const id = setInterval(() => { fetchPicks(true); }, 30 * 1000);
    return () => clearInterval(id);
  }, [isLiveEligible, fetchPicks]);

  const doRecompile = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/gold/daily-picks/compile`, null, {
        params: { date },
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchPicks();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const doSettle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/gold/daily-picks/settle`, null, {
        params: { date },
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchPicks();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const summary = data?.summary || {};
  const activeCat = CATS.find((c) => c.id === tab) || CATS[0];
  const picks = data?.[activeCat.key] || [];
  const activeSummary = summary[tab] || {};

  return (
    <Card
      className="border-amber-500/30 bg-gradient-to-br from-amber-950/30 via-black to-black overflow-hidden"
      data-testid="gold-daily-picks-panel"
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <Sparkles className="w-5 h-5" />
              <h2 className="text-lg font-bold tracking-widest uppercase">
                GOLD Daily Picks · Hit Tracker
              </h2>
              {isLiveEligible && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-widest font-bold"
                  data-testid="gold-picks-live-indicator"
                  title="Auto-refreshing every 30s while games are in play"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <div className="text-[11px] text-amber-500/60 mt-1">
              90 picks per day, ranked by the full 20-layer intelligence engine.
              Outcomes settle automatically every minute.
              {lastUpdatedAt && (
                <span className="ml-2 text-amber-500/50">
                  · Updated {lastUpdatedAt.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DateSelector date={date} setDate={setDate} />
            <Button
              size="sm" variant="outline"
              className="h-8 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={fetchPicks} disabled={loading}
              data-testid="gold-picks-refresh"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {(isAdmin || true) && (
              <>
                <Button
                  size="sm" variant="outline"
                  className="h-8 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  onClick={doRecompile} disabled={busy}
                  data-testid="gold-picks-recompile"
                >
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Recompile
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="h-8 text-[11px] border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  onClick={doSettle} disabled={busy}
                  data-testid="gold-picks-settle"
                >
                  Settle
                </Button>
              </>
            )}
          </div>
        </div>

        {err && (
          <div className="mb-3 p-2 rounded border border-red-500/30 bg-red-500/10 text-[12px] text-red-300" data-testid="gold-picks-error">
            {err}
          </div>
        )}

        {loading && !data && (
          <div className="p-8 text-center text-amber-400 animate-pulse">Loading picks…</div>
        )}

        {data && (
          <>
            {/* Overall summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              {CATS.map((c) => {
                const s = summary[c.id] || {};
                const rate = s.hit_rate_pct;
                const tone = rate === null ? "amber" : rate >= 70 ? "emerald" : rate >= 55 ? "amber" : "neutral";
                return (
                  <StatChip
                    key={c.id}
                    label={c.label}
                    value={rate === null || rate === undefined ? "—" : `${rate}%`}
                    hint={`${s.hits ?? 0}h / ${s.misses ?? 0}m · ${s.pending ?? 0} pending`}
                    tone={tone}
                  />
                );
              })}
            </div>

            {/* Category tabs */}
            <div className="flex gap-1 mb-3 flex-wrap">
              {CATS.map((c) => {
                const Icon = c.icon;
                const active = c.id === tab;
                const s = summary[c.id] || {};
                return (
                  <button
                    key={c.id}
                    onClick={() => setTab(c.id)}
                    data-testid={`gold-picks-tab-${c.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded border transition-colors ${
                      active
                        ? "bg-amber-500/25 text-amber-100 border-amber-400"
                        : "bg-black/60 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[12px] font-bold uppercase tracking-widest">{c.label}</span>
                    <span className="text-[10px] text-amber-500/60 tabular-nums">
                      {s.hits ?? 0}/{s.graded ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="text-[11px] text-amber-500/70 mb-2">{activeCat.subtitle}</div>

            <CategoryTable cat={activeCat} picks={picks} />

            <div className="mt-3 text-[10px] text-amber-500/60 flex items-center gap-3 flex-wrap">
              <span>
                Compiled: {data.compiled_at ? new Date(data.compiled_at).toLocaleString() : "—"}
              </span>
              <span>·</span>
              <span>Fixtures scanned: {data.fixtures_scanned}</span>
              <span>·</span>
              <span>Engine: {data.engine}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
